import React, { useState, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

function HowdidPage() {
  const [selectedSource, setSelectedSource] = useState("");
  const [otherText, setOtherText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const referralSources = [
    { id: "instagram", label: "Instagram" },
    { id: "facebook", label: "Facebook" },
    { id: "twitter", label: "Twitter/X" },
    { id: "reddit", label: "Reddit" },
    { id: "linkedin", label: "LinkedIn" },
    { id: "youtube", label: "YouTube" },
    { id: "google", label: "Google Search" },
    { id: "friend", label: "Friend or Family" },
    { id: "colleague", label: "Colleague" },
    { id: "advertisement", label: "Advertisement" },
    { id: "blog", label: "Blog or Article" },
    { id: "podcast", label: "Podcast" },
    { id: "event", label: "Event or Conference" },
    { id: "other", label: "Other" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSource) {
      setMessage("Please select how you found us.");
      return;
    }

    if (selectedSource === "other" && !otherText.trim()) {
      setMessage("Please specify how you found us.");
      return;
    }

    if (!user) {
      setMessage("Authentication error. Please try again.");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const referralData = {
        referralSource: selectedSource,
        referralDetails:
          selectedSource === "other"
            ? otherText.trim()
            : referralSources.find((s) => s.id === selectedSource)?.label,
        referralCompletedAt: new Date().toISOString(),
        referralCompleted: true,
      };

      await updateDoc(doc(db, "users", user.uid), referralData);

      setMessage("Thank you. Redirecting to profile setup…");

      setTimeout(() => {
        navigate("/setup-profile");
      }, 1500);
    } catch (error) {
      console.error("Error saving referral data:", error);
      setMessage("Failed to save. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">
            How did you find us?
          </h1>
          <p className="mt-2 text-[15px] text-neutral-500 font-normal">
            Help us understand how you discovered Residency Companion
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* iOS-style grouped list */}
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200/80 overflow-hidden">
            {referralSources.map((source, index) => (
              <label
                key={source.id}
                className={`flex items-center justify-between px-4 py-3.5 cursor-pointer active:bg-neutral-100 transition-colors ${
                  index !== referralSources.length - 1
                    ? "border-b border-neutral-100"
                    : ""
                } ${selectedSource === source.id ? "bg-neutral-50" : ""}`}
              >
                <span
                  className={`text-[15px] ${
                    selectedSource === source.id
                      ? "text-neutral-900 font-medium"
                      : "text-neutral-700"
                  }`}
                >
                  {source.label}
                </span>
                <input
                  type="radio"
                  name="referralSource"
                  value={source.id}
                  checked={selectedSource === source.id}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="sr-only"
                />
                {selectedSource === source.id ? (
                  <span className="w-5 h-5 rounded-full bg-neutral-900 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                ) : (
                  <span className="w-5 h-5 rounded-full border-2 border-neutral-300 flex-shrink-0" />
                )}
              </label>
            ))}
          </div>

          {/* Other — text field */}
          {selectedSource === "other" && (
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-200/80 overflow-hidden p-4">
              <label
                htmlFor="otherText"
                className="block text-[13px] font-medium text-neutral-500 uppercase tracking-wider mb-2"
              >
                Please specify
              </label>
              <input
                id="otherText"
                type="text"
                placeholder="How you found us…"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-[15px] text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-400/30 focus:border-neutral-400 transition-colors"
                autoFocus
              />
            </div>
          )}

          {/* Message */}
          {message && (
            <p
              className={`text-[14px] text-center ${
                message.includes("Redirecting")
                  ? "text-neutral-600"
                  : "text-neutral-700"
              }`}
            >
              {message}
            </p>
          )}

          {/* Primary button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-neutral-900 hover:bg-neutral-800 active:bg-neutral-950 disabled:bg-neutral-400 disabled:cursor-not-allowed text-white py-3.5 px-6 rounded-xl text-[15px] font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving…
              </>
            ) : (
              "Continue"
            )}
          </button>
        </form>

        {/* Progress */}
        <div className="flex justify-center items-center gap-1.5 mt-6">
          <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
          <span className="w-4 h-0.5 bg-neutral-300 rounded-full" />
          <span className="w-1.5 h-1.5 rounded-full bg-neutral-900" />
          <span className="w-4 h-0.5 bg-neutral-200 rounded-full" />
          <span className="w-1.5 h-1.5 rounded-full bg-neutral-200" />
        </div>
        <p className="text-center text-[13px] text-neutral-400 mt-2">
          Step 2 of 3
        </p>
      </div>
    </div>
  );
}

export default HowdidPage;
